import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import uniqBy from "lodash/uniqBy";

import { useQuery } from "@stadium/services/apollo-client";
import { useInfiniteScrollIntersectionObserverBased } from "@stadium/hooks/lib/use-infinite-scroll-intersectionObserver-based";

import { fetchBaseProductsList } from "@graphql/queries/stores/products/fetch-base-products-list";
import { searchProductsSerializer } from "@serializers/search-products";

import { useSendItemsContext } from "@contexts/send-items-context";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import {
  getCommonElasticSearchStoreVariables,
  getStoreParentId,
  getStoreID,
  getIsLegacyLockerShop,
} from "@store/selectors/store";
import { getCountry } from "@selectors/country";
import { getPricelessProductFetchVariables } from "@selectors/product";
import { getCommonGlobalCountryVariables, getLockerShopESQueryVars } from "@helpers/store";
import { isGlobalCountry } from "@stadium/helpers/country/utils";

import { shopOurCatalog } from "@constants/widgets";

const emptyArr = [];
const INITIAL_PAGE = 1;

const useFetchMenuIdBasedProductList = ({
  variables,
  maxProductsToFetch = 0,
  numberOfVisibleItems = 0,
  intent = "",
  gqlQuery = fetchBaseProductsList,
  initialProducts = emptyArr,
  initialTotal = 0,
  shouldSkip,
  shouldReplaceList,
}) => {
  const { isSwagCreationShopFlow } = useCreateSwagContext();
  const country = useSelector(getCountry);
  const countryCode = isSwagCreationShopFlow ? "" : country?.iso;
  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);
  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const isLegacyLockerShop = useSelector(getIsLegacyLockerShop);
  const pricelessVariables = useSelector(getPricelessProductFetchVariables);

  const [fetchedProducts, setFetchedProducts] = useState(initialProducts);
  const [totalProducts, setTotalProducts] = useState(initialTotal);
  const [hasMore, setHasMore] = useState(
    Math.min(maxProductsToFetch, initialTotal) > initialProducts.length,
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { volumeDiscountProductsVariables } = useSendItemsContext();

  const isShopOurCatalogListing = intent === shopOurCatalog;

  const [loadMoreRef, page, setPage] = useInfiniteScrollIntersectionObserverBased(
    hasMore && !isLoadingMore,
    INITIAL_PAGE,
  );

  const { perPage } = variables;

  const productLimit = useMemo(() => {
    if (numberOfVisibleItems && isShopOurCatalogListing) {
      const fetchedProductsCount = fetchedProducts?.length;
      const remainingProductsToBeFetched = numberOfVisibleItems - fetchedProductsCount;
      return fetchedProductsCount === 0 || remainingProductsToBeFetched > perPage
        ? null
        : remainingProductsToBeFetched;
    }
  }, [page]);

  const gqlVariables = useMemo(
    () => ({
      ...variables,
      storeId: storeParentId,
      designedStoreIds: [storeId],
      ...getCommonGlobalCountryVariables({
        country: { iso: countryCode },
        isGlobalCountry: isGlobalCountry({ iso: countryCode }),
        countryKeyAttr: "countryCode",
      }),
      page,
      skipAggregations: true,
      limit: productLimit,
      showVariantsAsProducts: true,
      ...commonESVariables,
      includeProductVariants: false,
      ...getLockerShopESQueryVars({
        isLockerShop: isLegacyLockerShop,
      }),
      ...pricelessVariables,
      ...volumeDiscountProductsVariables,
    }),
    [
      page,
      storeId,
      isLegacyLockerShop,
      countryCode,
      volumeDiscountProductsVariables,
      pricelessVariables,
      productLimit,
      variables,
    ],
  );

  const { loading } = useQuery(gqlQuery, {
    variables: gqlVariables,
    fetchPolicy: "cache-and-network",
    skip: shouldSkip?.(page) || false,
    onCompleted(data) {
      const products = searchProductsSerializer(data?.baseSearch?.products || emptyArr);
      const total = data?.baseSearch?.total || 0;

      setFetchedProducts((prevProducts) => {
        const updatedProducts = shouldReplaceList
          ? products
          : uniqBy([...prevProducts, ...products], (obj) => obj.id);
        const hasMoreProducts = Math.min(maxProductsToFetch, total) > updatedProducts.length;
        setHasMore(hasMoreProducts);
        return updatedProducts;
      });

      setTotalProducts(total);
      setIsLoadingMore(false);
    },
  });

  const resetProductList = () => {
    setFetchedProducts(initialProducts);
    setTotalProducts(initialTotal);
    setHasMore(Math.min(maxProductsToFetch, initialTotal) > initialProducts.length);
    setPage(INITIAL_PAGE);
  };

  useEffect(() => {
    if (page > INITIAL_PAGE && !isLoadingMore) {
      setIsLoadingMore(true);
    }
  }, [page]);

  return {
    loading: loading || isLoadingMore,
    fetchedProducts,
    totalProducts,
    loadMoreRef,
    resetProductList,
  };
};

export default useFetchMenuIdBasedProductList;
